import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom"; // v5
import ajaxService from "../../../services/ajax-service";
import dayjs from "dayjs";
import moment from "moment";
import {
  Card,
  Spin,
  Alert,
  Button,
  Avatar,
  Tabs,
  List,
  Tag,
  Empty,
  Typography,
  Table,
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  HeartOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  ArrowLeftOutlined,
  CalendarOutlined,
  IdcardOutlined,
} from "@ant-design/icons";

/** ---------- Small Presentational Pieces ---------- **/

const VerificationStatusTag = ({ status }) => {
  const normalized = status === "" ? null : status;

  if (!normalized) return <Tag>None</Tag>;

  const lower = String(normalized).toLowerCase();

  if (lower === "approved") return <Tag color="green">Approved</Tag>;
  if (lower === "pending") return <Tag color="gold">Pending</Tag>;
  if (lower === "rejected") return <Tag color="red">Rejected</Tag>;

  return <Tag color="blue">{String(normalized)}</Tag>;
};

const StripeVerificationTag = ({ status }) => {
  const raw = status === "" ? null : status;
  if (!raw) return <Tag>None</Tag>;

  const lower = String(raw).trim().toLowerCase();
  const isAllVerified =
    lower === "all_verified" ||
    lower === "all verified" ||
    lower === "verified" ||
    lower === "completed" ||
    lower === "complete";

  if (isAllVerified) return <Tag color="green">All verified</Tag>;
  if (lower === "pending" || lower === "incomplete") return <Tag color="gold">Pending</Tag>;
  if (lower === "rejected" || lower === "failed") return <Tag color="red">Failed</Tag>;

  return <Tag color="blue">{String(raw)}</Tag>;
};

const normalizeStatus = (status) => {
  if (status === "" || status === null || status === undefined) return "";
  return String(status).trim().toLowerCase();
};

const getVerificationPill = (user) => {
  const manual = normalizeStatus(user?.manual_verification_status);
  const stripe = normalizeStatus(user?.stripe_verification_status);

  const stripeVerified =
    stripe === "all_verified" ||
    stripe === "all verified" ||
    stripe === "verified" ||
    stripe === "completed" ||
    stripe === "complete";

  // Prefer explicit manual status when present; fall back to Stripe.
  if (manual) {
    if (manual === "approved") return { text: "Verified", variant: "verified" };
    if (manual === "pending") return { text: "Pending", variant: "muted" };
    if (manual === "rejected") return { text: "Rejected", variant: "muted" };
    return { text: String(user?.manual_verification_status || "Unknown"), variant: "muted" };
  }

  if (stripe) {
    if (stripeVerified) return { text: "Verified", variant: "verified" };
    if (stripe === "pending" || stripe === "incomplete") return { text: "Pending", variant: "muted" };
    if (stripe === "rejected" || stripe === "failed") return { text: "Failed", variant: "muted" };
    return { text: String(user?.stripe_verification_status || "Unknown"), variant: "muted" };
  }

  return { text: "Not verified", variant: "muted" };
};

const InfoTile = ({ label, value, span2 = false }) => (
  <div className={`ud-info-tile ${span2 ? "ud-span-2" : ""}`}>
    <div className="ud-info-label">{label}</div>
    <div className="ud-info-value">{value}</div>
  </div>
);

const UserInfo = ({ user }) => {
  const phoneValue =
    user?.phone
      ? `${user?.country_code ? `+${user.country_code} ` : ""}${user.phone}`
      : "N/A";

  const locationValue =
    user?.city?.name || user?.country_name
      ? [user?.city?.name, user?.country_name].filter(Boolean).join(", ")
      : "N/A";

  const dobValue = user?.dob ? dayjs(user.dob).format("MMMM D, YYYY") : "N/A";
  const genderValue = user?.gender || "N/A";
  const ageValue = user?.age ?? "N/A";
  const bioValue = user?.bio || "N/A";

  return (
    <div className="ud-info-grid">
      <InfoTile label="Phone" value={phoneValue} />
      <InfoTile label="Gender" value={genderValue} />
      <InfoTile label="Date of Birth" value={dobValue} />
      <InfoTile label="Age" value={ageValue} />
      <InfoTile label="Location" value={locationValue} span2 />
      <InfoTile label="Bio" value={bioValue} span2 />
    </div>
  );
};

const RolesList = ({ roles = [] }) =>
  roles.length ? (
    <List
      dataSource={roles}
      renderItem={(role) => (
        <Tag
          color="blue"
          icon={<CheckCircleOutlined />}
          key={role?.id || role?.name}
          style={{ marginBottom: 'var(--spacing-sm)' }}
        >
          {role?.name}
        </Tag>
      )}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No roles" />
  );

  const ReferredByList = ({ referredBy = [], loading = false }) => {
  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    if (referredBy && referredBy.length > 0) {
      setDataSource(referredBy);
    } else {
      setDataSource([]);
    }
  }, [referredBy]);

  const columns = [
    {
      title: 'SNo',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      fixed: 'left',
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (name) => <Typography.Text style={{ fontSize: '0.85rem' }}>{name || 'N/A'}</Typography.Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 300,
      ellipsis: true,
      render: (email) => <Typography.Text style={{ fontSize: '0.85rem' }}>{email || 'N/A'}</Typography.Text>,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      ellipsis: true,
      render: (phone, record) => (
        <Typography.Text style={{ fontSize: '0.85rem' }}>
          {record?.country_code ? '+' + record.country_code + ' ' + (phone || '') : (phone || 'N/A')}
        </Typography.Text>
      ),
    },
    {
      title: 'Referral Code',
      dataIndex: 'referral_code',
      key: 'referral_code',
      width: 150,
      ellipsis: true,
      render: (referral_code) => <Typography.Text style={{ fontSize: '0.85rem' }}>{referral_code || 'N/A'}</Typography.Text>,
    },
    {
      title: 'Plan',
      dataIndex: 'stripe_id',
      key: 'stripe_id',
      width: 150,
      render: (stripe_id) => (
        <Typography.Text style={{ fontSize: '0.85rem' }}>
          {stripe_id ? 'Premium' : 'Free'}
        </Typography.Text>
      ),
      filters: [
        { text: 'Free User', value: 'free' },
        { text: 'Paid User', value: 'paid' },
      ],
      onFilter: (value, record) => value === 'free' ? !record.stripe_id : !!record.stripe_id,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 150,
      render: (active) => (
        <Typography.Text style={{ fontSize: '0.85rem' }}>
          {(active === '1' || active === 1) ? 'Active' : 'Inactive'}
        </Typography.Text>
      ),
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ],
      onFilter: (value, record) => {
        const isActive = record.active === '1' || record.active === 1;
        return value === 'active' ? isActive : !isActive;
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      fixed: 'right',
      render: (created_at) => (
        <Typography.Text style={{ fontSize: '0.85rem' }}>
          {created_at ? moment(created_at).format('DD/MM/YY') : 'N/A'}
        </Typography.Text>
      ),
    },
  ];

  if (!loading && referredBy.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No referred by users found" />;
  }

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      rowKey="id"
      loading={loading}
      pagination={false}
      size="small"
      scroll={{ x: 1060 }}
    />
  );
};

const InterestsList = ({ interestUsers = [] }) =>
  interestUsers.length ? (
    <List
      grid={{ gutter: 12, column: 2 }}
      dataSource={interestUsers}
      renderItem={(iu, idx) => (
        <List.Item key={`${iu?.interest?.id}-${iu?.interest_type?.id}-${idx}`}>
          <Tag color="geekblue" icon={<HeartOutlined />}>
            {iu?.interest?.name || "Unknown"}{" "}
            {iu?.interest_type?.name ? `(${iu.interest_type.name})` : ""}
          </Tag>
        </List.Item>
      )}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No interests" />
  );

const LifestyleAnswersList = ({ answers = [] }) =>
  answers.length ? (
    <List
      itemLayout="vertical"
      dataSource={answers}
      renderItem={(ans) => (
        <Card
          size="small"
          style={{
            marginBottom: 'var(--spacing-sm)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-card)',
            background: 'var(--dark-bg-card)',
            border: '1px solid rgba(254, 2, 125, 0.2)',
          }}
          title={<span style={{ color: 'var(--text-white)' }}>{ans?.lifestyle?.question || `Question ${ans?.question_id}`}</span>}
        >
          <p style={{ fontWeight: 500, color: 'var(--text-white)' }}>{ans?.answer ?? "N/A"}</p>
        </Card>
      )}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No lifestyle answers" />
  );

const QuestionAnswersList = ({ answers = [] }) =>
  answers.length ? (
    <List
      itemLayout="vertical"
      dataSource={answers}
      renderItem={(ans) => (
        <Card
          size="small"
          style={{
            marginBottom: 'var(--spacing-sm)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-card)',
            background: 'var(--dark-bg-card)',
            border: '1px solid rgba(254, 2, 125, 0.2)',
          }}
          title={<span style={{ color: 'var(--text-white)' }}>{ans?.question?.question || `Question ${ans?.question_id}`}</span>}
        >
          <p style={{ fontWeight: 500, color: 'var(--text-white)' }}>{ans?.answer ?? "N/A"}</p>
        </Card>
      )}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No custom answers" />
  );

const ReferralsList = ({ referrals = [], loading = false }) => {
  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    if (referrals && referrals.length > 0) {
      setDataSource(referrals);
    } else {
      setDataSource([]);
    }
  }, [referrals]);

  const columns = [
    {
      title: 'SNo',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      fixed: 'left',
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      ellipsis: true,
      render: (name) => <Typography.Text style={{ fontSize: '0.85rem' }}>{name || 'N/A'}</Typography.Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 300,
      ellipsis: true,
      render: (email) => <Typography.Text style={{ fontSize: '0.85rem' }}>{email || 'N/A'}</Typography.Text>,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 200,
      ellipsis: true,
      render: (phone, record) => (
        <Typography.Text style={{ fontSize: '0.85rem' }}>
          {record?.country_code ? '+' + record.country_code + ' ' + (phone || '') : (phone || 'N/A')}
        </Typography.Text>
      ),
    },
    {
      title: 'Referral Code',
      dataIndex: 'referral_code',
      key: 'referral_code',
      width: 250,
      ellipsis: true,
      render: (referral_code) => <Typography.Text style={{ fontSize: '0.85rem' }}>{referral_code || 'N/A'}</Typography.Text>,
    },
    {
      title: 'Plan',
      dataIndex: 'stripe_id',
      key: 'stripe_id',
      width: 100,
      render: (stripe_id) => (
        <Typography.Text style={{ fontSize: '0.85rem' }}>
          {stripe_id ? 'Premium' : 'Free'}
        </Typography.Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 150,
      render: (active) => (
        <Typography.Text style={{ fontSize: '0.85rem' }}>
          {(active === '1' || active === 1) ? 'Active' : 'Inactive'}
        </Typography.Text>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      fixed: 'right',
      render: (created_at) => (
        <Typography.Text style={{ fontSize: '0.85rem' }}>
          {created_at ? moment(created_at).format('DD/MM/YY') : 'N/A'}
        </Typography.Text>
      ),
    },
  ];

  if (!loading && referrals.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No referrals found" />;
  }

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      rowKey="id"
      loading={loading}
      pagination={false}
      size="small"
      scroll={{ x: 1060 }}
    />
  );
};

/** ---------- Main Component ---------- **/

const UserDetails = () => {
  const { id } = useParams();
  const history = useHistory(); // v5

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referredBy, setReferredBy] = useState([]);
  const [referredByLoading, setReferredByLoading] = useState(false);

  const fetchUser = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ajaxService.get(`/users/${userId}`);
      if (response?.success) {
        setUser(response.data || {});
      } else {
        setError(response?.message || "Failed to fetch user data.");
        setUser(null);
      }
    } catch (err) {
      console.error("Error fetching user:", err);
      setError("Error fetching user data.");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReferrals = useCallback(async (userId) => {
    setReferralsLoading(true);
    try {
      const response = await ajaxService.get(`/user/${userId}/referrals`);
      if (response?.success || response?.status) {
        // The API returns data in response.data.data (pagination structure with nested data)
        // Each item has a referred_user object with user details
        const referralsData = response?.data?.data || [];
        // Transform the data to flatten referred_user
        const transformedReferrals = referralsData.map(item => ({
          id: item.referred_user?.id,
          name: item.referred_user?.name,
          email: item.referred_user?.email,
          phone: item.referred_user?.phone,
          country_code: item.referred_user?.country?.country_code,
          referral_code: item.referred_user?.referral_code,
          stripe_id: item.referred_user?.stripe_id,
          active: item.referred_user?.active,
          created_at: item.referred_user?.created_at,
        }));
        setReferrals(transformedReferrals);
      } else {
        setReferrals([]);
      }
    } catch (err) {
      console.error("Error fetching referrals:", err);
      setReferrals([]);
    } finally {
      setReferralsLoading(false);
    }
  }, []);

  const fetchReferredBy = useCallback(async (userId) => {
    setReferredByLoading(true);
    try {
      const response = await ajaxService.get(`/user/${userId}/referred`);
      if (response?.success || response?.status) {
        // The API returns data directly in response.data.data
        const referredByData = response?.data || [];
        // Data is flat - no need to transform
        setReferredBy(referredByData);
      } else {
        setReferredBy([]);
      }
    } catch (err) {
      console.error("Error fetching referred by:", err);
      setReferredBy([]);
    } finally {
      setReferredByLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchUser(id);
      fetchReferrals(id);
      fetchReferredBy(id);
    }
  }, [id, fetchUser, fetchReferrals, fetchReferredBy]);

  // Listen for sidebar collapse state
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
  }, []);

  const avatarSrc = useMemo(() => {
    const src = user?.profile_image;
    return src && String(src).trim() ? src : undefined;
  }, [user?.profile_image]);

  const verificationPill = useMemo(() => getVerificationPill(user), [
    user?.manual_verification_status,
    user?.stripe_verification_status,
  ]);

  const isVerifiedApproved = useMemo(() => verificationPill.variant === "verified", [verificationPill.variant]);

  const memberSinceLabel = useMemo(() => {
    const createdAt = user?.created_at;
    return createdAt ? dayjs(createdAt).format("MMM YYYY") : "N/A";
  }, [user?.created_at]);

  const planLabel = useMemo(() => (user?.stripe_id ? "premium-user" : "free-user"), [user?.stripe_id]);

  if (error) {
    return (
      <div className="page-container" style={{ padding: 'var(--spacing-xl)', display: 'flex', justifyContent: 'center' }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginTop: 'var(--spacing-2xl)', maxWidth: '600px', width: '100%' }}
        />
      </div>
    );
  }

  if (!user && loading) {
    return (
      <div className="page-container" style={{ padding: 'var(--spacing-xl)', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user && !loading) {
    return (
      <div className="page-container" style={{ padding: 'var(--spacing-xl)', display: 'flex', justifyContent: 'center' }}>
        <Alert
          message="No user found"
          type="warning"
          showIcon
          style={{ marginTop: 'var(--spacing-2xl)', maxWidth: '600px', width: '100%' }}
        />
      </div>
    );
  }

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Spin spinning={loading}>
        <Card
          className="ud-user-details-card"
          style={{
            width: '100%',
            maxWidth: '100%',
            margin: '0 auto',
            borderRadius: 'var(--radius-2xl)',
            boxShadow: 'var(--shadow-card)',
            background: 'var(--dark-bg-card)',
            border: '1px solid rgba(254, 2, 125, 0.2)',
          }}
          title={<span style={{ fontSize: '1.25rem', fontFamily: 'Poppins-Bold', color: 'var(--text-white)' }}>User Details</span>}
          extra={
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => history.goBack()}
              style={{
                background: 'var(--primary-purple)',
                borderColor: 'var(--primary-purple)',
                color: 'white',
              }}
            >
              Back
            </Button>
          }
        >
          {/* Top section */}
          <div className="ud-top-grid">
            <Card
              className="ud-profile-card"
              title={null}
              headStyle={{ display: "none" }}
              style={{
                borderRadius: "var(--radius-2xl)",
                boxShadow: "var(--shadow-card)",
                background: "var(--dark-bg-card)",
                border: "1px solid rgba(254, 2, 125, 0.2)",
              }}
              bodyStyle={{ padding: 0 }}
            >
              <div className="ud-profile-wrap">
                <div className="ud-profile-header">
                  <div className="ud-avatar-wrap">
                    <Avatar
                      size={120}
                      icon={<UserOutlined />}
                      src={avatarSrc}
                      style={{
                        border: "2px solid var(--primary-purple)",
                        boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
                      }}
                    />
                  </div>

                  <div className="ud-profile-title">
                    <div className="ud-name">{user?.name || "N/A"}</div>
                    <div className="ud-email">{user?.email || "N/A"}</div>
                    <div className="ud-verified-pill-row">
                      {isVerifiedApproved ? (
                        <span className="ud-verified-pill">
                          <CheckCircleFilled style={{ color: "var(--success-green)", fontSize: 16 }} />
                          {verificationPill.text}
                        </span>
                      ) : (
                        <span className="ud-verified-pill ud-verified-pill--muted">{verificationPill.text}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="ud-profile-meta">
                  <div className="ud-meta-row">
                    <div className="ud-meta-chip">
                      <CalendarOutlined style={{ color: "var(--text-gray)" }} />
                      <span>Member since {memberSinceLabel}</span>
                    </div>
                  </div>

                  <div className="ud-meta-row ud-meta-row--chips">
                    <Tag className="ud-soft-tag" icon={<IdcardOutlined />}>
                      {user?.referral_code || "N/A"}
                    </Tag>
                    <Tag className="ud-soft-tag" color="blue">
                      {planLabel}
                    </Tag>
                  </div>

                  {/* Actions removed per UI request */}
                </div>
              </div>
            </Card>

            <Card
              className="ud-user-info-card"
              title={null}
              headStyle={{ display: "none" }}
              style={{
                borderRadius: "var(--radius-2xl)",
                boxShadow: "var(--shadow-card)",
                background: "var(--dark-bg-card)",
                border: "1px solid rgba(254, 2, 125, 0.2)",
              }}
              bodyStyle={{ padding: 0 }}
            >
              <div className="ud-info-wrap">
                <UserInfo user={user} />
              </div>
            </Card>
          </div>

          {/* Tabbed Section */}
          <Tabs 
            defaultActiveKey="roles" 
            style={{ marginTop: 'var(--spacing-xl)' }}
            items={[
              {
                key: 'roles',
                label: <span><TeamOutlined /> Roles</span>,
                children: <RolesList roles={user?.roles} />,
              },
              {
                key: 'interests',
                label: <span><HeartOutlined /> Interests</span>,
                children: <InterestsList interestUsers={user?.interest_users} />,
              },
              {
                key: 'lifestyle',
                label: <span><QuestionCircleOutlined /> Lifestyle</span>,
                children: <LifestyleAnswersList answers={user?.lifestyle_answers} />,
              },
              {
                key: 'questions',
                label: <span><QuestionCircleOutlined /> Custom Questions</span>,
                children: <QuestionAnswersList answers={user?.question_answers} />,
              },
              {
                key: 'referrals',
                label: <span><TeamOutlined /> Referrals ({referrals.length})</span>,
                children: <ReferralsList referrals={referrals} loading={referralsLoading} />,
              },
              {
                key: 'referred-by',
                label: <span><TeamOutlined /> Referred By ({referredBy.length})</span>,
                children: <ReferredByList referredBy={referredBy} loading={referredByLoading} />,
              },
            ]}
          />
        </Card>
      </Spin>
    </div>
  );
};

export default UserDetails;
